import { useCallback, useEffect, useState } from "react";
import { api, type APIResult } from "../../api/client";
import { useAppState } from "../../hooks/useAppState";
import {
  classNames,
  formatDateTime,
  formatJSON,
  formatRelativeTime,
  shortId,
  truncate,
} from "../../lib/format";
import { PageHeader } from "../PageHeader";
import { SectionCard } from "../SectionCard";
import type {
  DeadLetterDetail,
  DeadLetterListItem,
  DeadLetterListResponse,
  ReplayResponse,
} from "../../types/routecore";

// Tab "Dead Letters" — UI administrativa para inspecionar e reprocessar
// jobs que falharam definitivamente.
//
// Fluxo:
//   1. Filtros (status default = active) → lista paginada.
//   2. Click "Detalhe" abre slide-over com original_event + stack_trace.
//   3. Replay/Discard com confirmação destrutiva.
//
// TODO(ux): trocar window.confirm/prompt por modal stylizado custom em
// uma próxima iteração — MVP usa nativos para evitar dependência nova.

type Props = {
  onNavigate: (tabId: string) => void;
};

type Filters = {
  errorCode: string;
  status: string;
  since: string;
  until: string;
};

type Toast = {
  kind: "info" | "success" | "error";
  message: string;
  /** quando definido, vira link "abrir" ao lado da mensagem. */
  jobId?: string;
};

const PAGE_SIZE = 50;

const DEFAULT_FILTERS: Filters = {
  errorCode: "",
  status: "active",
  since: "",
  until: "",
};

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "active", label: "Ativos" },
  { value: "replayed", label: "Replayed" },
  { value: "discarded", label: "Descartados" },
  { value: "all", label: "Todos" },
];

const errorCodeBadge = (code: string): string => {
  // Heurística simples: códigos com TIMEOUT/RATE em amarelo, demais em rose.
  if (/TIMEOUT|RATE/i.test(code)) return "bg-amber-100 text-amber-800";
  if (/PANIC|FATAL/i.test(code)) return "bg-rose-200 text-rose-900";
  return "bg-rose-100 text-rose-800";
};

// isHandled detecta o estado terminal de uma dead-letter (já tratada
// por replay ou discard). O upstream omite os campos quando nulos —
// por isso usamos truthy-check em vez de comparar com null.
const isReplayed = (d: { replayed_at?: string | null }): boolean => Boolean(d.replayed_at);
const isDiscarded = (d: { discarded_at?: string | null }): boolean => Boolean(d.discarded_at);
const isHandled = (d: {
  replayed_at?: string | null;
  discarded_at?: string | null;
}): boolean => isReplayed(d) || isDiscarded(d);

const statusBorderColor = (item: DeadLetterListItem): string => {
  if (isDiscarded(item)) return "border-l-ink-400";
  if (isReplayed(item)) return "border-l-sky-500";
  return "border-l-emerald-500";
};

const statusLabel = (item: DeadLetterListItem): { text: string; cls: string } => {
  if (isDiscarded(item)) return { text: "discarded", cls: "bg-ink-200 text-ink-700" };
  if (isReplayed(item)) return { text: "replayed", cls: "bg-sky-100 text-sky-800" };
  return { text: "active", cls: "bg-emerald-100 text-emerald-800" };
};

// extractErrorDetails normaliza o corpo de erro do upstream. A
// RouteCore usa `{ error: { code, message, details } }` mas alguns
// proxies/wrappers podem entregar `{ details }` no topo — cobrimos
// ambos para evitar regressão silenciosa.
function extractErrorDetails(data: unknown): Record<string, unknown> {
  if (!data || typeof data !== "object") return {};
  const obj = data as Record<string, unknown>;
  if (obj.error && typeof obj.error === "object") {
    const err = obj.error as Record<string, unknown>;
    if (err.details && typeof err.details === "object") {
      return err.details as Record<string, unknown>;
    }
  }
  if (obj.details && typeof obj.details === "object") {
    return obj.details as Record<string, unknown>;
  }
  return {};
}

export function TabDeadLetters({ onNavigate }: Props) {
  const { setTrackedJobId, pushJobId } = useAppState();
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [applied, setApplied] = useState<Filters>(DEFAULT_FILTERS);
  const [offset, setOffset] = useState(0);
  const [result, setResult] = useState<APIResult<DeadLetterListResponse> | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const [actionInFlight, setActionInFlight] = useState(false);

  const fetchList = useCallback(
    async (reset: boolean = false) => {
      setLoading(true);
      const nextOffset = reset ? 0 : offset;
      const r = await api.listDeadLetters({
        error_code: applied.errorCode || undefined,
        status: applied.status === "all" ? undefined : applied.status,
        since: applied.since || undefined,
        until: applied.until || undefined,
        limit: PAGE_SIZE,
        offset: nextOffset,
      });
      setResult(r);
      if (reset) setOffset(0);
      setLoading(false);
    },
    [applied, offset],
  );

  // Fetch ao montar e sempre que filtros aplicados ou offset mudarem.
  useEffect(() => {
    void fetchList(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied, offset]);

  // Auto-dismiss do toast após 6s.
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(id);
  }, [toast]);

  function applyFilters() {
    setOffset(0);
    setApplied(filters);
  }

  function clearFilters() {
    setFilters(DEFAULT_FILTERS);
    setApplied(DEFAULT_FILTERS);
    setOffset(0);
  }

  async function handleReplay(id: string, opts: { closeDrawer: boolean }) {
    if (!window.confirm("Replay vai criar um novo job a partir do evento original. Continuar?")) {
      return;
    }
    setActionInFlight(true);
    const r = await api.replayDeadLetter(id);
    setActionInFlight(false);
    if (r.ok && r.data) {
      const data = r.data as ReplayResponse;
      pushJobId(data.new_job_id);
      setToast({
        kind: "success",
        message: `Replay criado — job_id ${shortId(data.new_job_id)}`,
        jobId: data.new_job_id,
      });
      if (opts.closeDrawer) setSelectedId(null);
      void fetchList(false);
    } else if (r.status === 409) {
      const details = extractErrorDetails(r.data);
      const replayedJob = details["replayed_job_id"];
      const replayedAt = details["replayed_at"];
      const discardReason = details["discard_reason"];
      const discardedAt = details["discarded_at"];
      let msg = "Conflito: ação não permitida neste estado";
      if (replayedJob) {
        msg = `Já replayed${replayedAt ? ` em ${formatDateTime(String(replayedAt))}` : ""} — job atual: ${shortId(String(replayedJob))}`;
      } else if (discardReason) {
        msg = `Já descartado${discardedAt ? ` em ${formatDateTime(String(discardedAt))}` : ""}: ${String(discardReason)}`;
      }
      setToast({
        kind: "error",
        message: msg,
        jobId: replayedJob ? String(replayedJob) : undefined,
      });
    } else {
      setToast({ kind: "error", message: `Falha ao replay (HTTP ${r.status})` });
    }
  }

  async function handleDiscard(id: string, opts: { closeDrawer: boolean }) {
    const reason = window.prompt(
      "Discard marca como descartado, sem replay. Motivo (obrigatório):",
      "",
    );
    if (reason === null) return;
    const trimmed = reason.trim();
    if (!trimmed) {
      setToast({ kind: "error", message: "Motivo obrigatório para discard" });
      return;
    }
    setActionInFlight(true);
    const r = await api.discardDeadLetter(id, trimmed);
    setActionInFlight(false);
    if (r.ok) {
      setToast({ kind: "success", message: "Dead letter descartada" });
      if (opts.closeDrawer) setSelectedId(null);
      void fetchList(false);
    } else if (r.status === 409) {
      const details = extractErrorDetails(r.data);
      const replayedJob = details["replayed_job_id"];
      const replayedAt = details["replayed_at"];
      const discardReason = details["discard_reason"];
      let msg = "Conflito: ação não permitida neste estado";
      if (replayedJob) {
        msg = `Já replayed${replayedAt ? ` em ${formatDateTime(String(replayedAt))}` : ""} — job atual: ${shortId(String(replayedJob))}`;
      } else if (discardReason) {
        msg = `Já descartado: ${String(discardReason)}`;
      }
      setToast({
        kind: "error",
        message: msg,
        jobId: replayedJob ? String(replayedJob) : undefined,
      });
    } else {
      setToast({ kind: "error", message: `Falha ao discard (HTTP ${r.status})` });
    }
  }

  function jumpToJob(jobId: string) {
    setTrackedJobId(jobId);
    pushJobId(jobId);
    onNavigate("job-status");
  }

  const items = result?.data?.items ?? [];
  const total = result?.data?.total ?? 0;
  const limit = result?.data?.limit ?? PAGE_SIZE;
  const showingFrom = total === 0 ? 0 : offset + 1;
  const showingTo = Math.min(offset + items.length, total);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Dead Letters"
        subtitle="Jobs que falharam definitivamente após esgotar retries. Use replay para reprocessar ou discard para marcar como tratado."
        endpoint={{ method: "GET", path: "/v1/admin/dead-letters" }}
      />

      <SectionCard
        title="Filtros"
        description="Filtre por código de erro, status do tratamento e janela de tempo."
        actions={
          <div className="flex items-center gap-2">
            <button type="button" className="ks-btn-ghost text-xs" onClick={clearFilters}>
              limpar
            </button>
            <button type="button" className="ks-btn-primary text-xs" onClick={applyFilters}>
              Aplicar
            </button>
          </div>
        }
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="ks-label" htmlFor="dl-error-code">
              Código de erro
            </label>
            <input
              id="dl-error-code"
              type="text"
              className="ks-input ks-code mt-1"
              placeholder="ex.: SOLVER_TIMEOUT"
              value={filters.errorCode}
              onChange={(e) => setFilters({ ...filters, errorCode: e.target.value })}
              list="dl-error-code-suggestions"
            />
            <datalist id="dl-error-code-suggestions">
              <option value="SOLVER_TIMEOUT" />
              <option value="UPSTREAM_UNAVAILABLE" />
              <option value="VALIDATION_FAILED" />
              <option value="PANIC" />
              <option value="RATE_LIMITED" />
            </datalist>
          </div>
          <div>
            <label className="ks-label" htmlFor="dl-status">
              Status
            </label>
            <select
              id="dl-status"
              className="ks-input mt-1"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="ks-label" htmlFor="dl-since">
              Desde
            </label>
            <input
              id="dl-since"
              type="datetime-local"
              className="ks-input mt-1"
              value={filters.since}
              onChange={(e) => setFilters({ ...filters, since: e.target.value })}
            />
          </div>
          <div>
            <label className="ks-label" htmlFor="dl-until">
              Até
            </label>
            <input
              id="dl-until"
              type="datetime-local"
              className="ks-input mt-1"
              value={filters.until}
              onChange={(e) => setFilters({ ...filters, until: e.target.value })}
            />
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title="Resultados"
        description={
          loading
            ? "Carregando..."
            : total === 0
              ? "Nenhuma dead letter encontrada com esses filtros."
              : `${showingFrom}-${showingTo} de ${total}`
        }
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="ks-btn-secondary text-xs"
              disabled={loading || offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}
            >
              ← anterior
            </button>
            <button
              type="button"
              className="ks-btn-secondary text-xs"
              disabled={loading || offset + items.length >= total}
              onClick={() => setOffset(offset + limit)}
            >
              próxima →
            </button>
            <button
              type="button"
              className="ks-btn-ghost text-xs"
              disabled={loading}
              onClick={() => fetchList(false)}
            >
              atualizar
            </button>
          </div>
        }
      >
        {result && !result.ok && (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
            HTTP {result.status} — não foi possível listar dead-letters. Verifique se o BFF está
            conectado.
          </div>
        )}
        {result?.ok && items.length === 0 && !loading && (
          <p className="text-sm text-ink-500">Sem registros para exibir.</p>
        )}
        {items.length > 0 && (
          <ul className="space-y-2">
            {items.map((item) => {
              const status = statusLabel(item);
              return (
                <li
                  key={item.id}
                  className={classNames(
                    "rounded-lg border border-ink-200 bg-white px-4 py-3 shadow-sm transition border-l-4",
                    statusBorderColor(item),
                  )}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`ks-tag ${errorCodeBadge(item.error_code)}`}>
                          {item.error_code}
                        </span>
                        <span className={`ks-tag ${status.cls}`}>{status.text}</span>
                        <span
                          className="text-xs text-ink-500"
                          title={formatDateTime(item.failed_at)}
                        >
                          {formatRelativeTime(item.failed_at)}
                        </span>
                        <span className="ks-tag bg-ink-100 text-ink-700">
                          {item.attempts} tentativa{item.attempts === 1 ? "" : "s"}
                        </span>
                      </div>
                      <p
                        className="mt-1 text-sm text-ink-800"
                        title={item.error_message}
                      >
                        {truncate(item.error_message, 80)}
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-3 text-[11px] text-ink-500">
                        <span>
                          job:{" "}
                          <button
                            type="button"
                            className="ks-code text-brand-700 hover:underline"
                            onClick={() => jumpToJob(item.job_id)}
                            title={item.job_id}
                          >
                            {shortId(item.job_id)}
                          </button>
                        </span>
                        <span>
                          external_request_id:{" "}
                          <code className="ks-code text-ink-700">
                            {truncate(item.external_request_id, 32)}
                          </code>
                        </span>
                        <span>worker: {item.worker_version}</span>
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                      <button
                        type="button"
                        className="ks-btn-secondary text-xs"
                        onClick={() => setSelectedId(item.id)}
                      >
                        Detalhe
                      </button>
                      <button
                        type="button"
                        className="ks-btn-primary text-xs"
                        disabled={actionInFlight || isHandled(item)}
                        onClick={() => handleReplay(item.id, { closeDrawer: false })}
                        title={
                          isReplayed(item)
                            ? "já replayed"
                            : isDiscarded(item)
                              ? "descartada"
                              : "criar novo job"
                        }
                      >
                        Replay
                      </button>
                      <button
                        type="button"
                        className="ks-btn-secondary text-xs text-rose-700 hover:bg-rose-50"
                        disabled={actionInFlight || isHandled(item)}
                        onClick={() => handleDiscard(item.id, { closeDrawer: false })}
                      >
                        Discard
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </SectionCard>

      {selectedId && (
        <DeadLetterDrawer
          id={selectedId}
          onClose={() => setSelectedId(null)}
          onReplay={() => handleReplay(selectedId, { closeDrawer: true })}
          onDiscard={() => handleDiscard(selectedId, { closeDrawer: true })}
          onJumpToJob={jumpToJob}
          actionInFlight={actionInFlight}
        />
      )}

      {toast && (
        <ToastBanner
          toast={toast}
          onDismiss={() => setToast(null)}
          onOpenJob={(id) => {
            jumpToJob(id);
            setToast(null);
          }}
        />
      )}
    </div>
  );
}

// Slide-over com o detalhe da dead-letter. Carrega o GET /:id ao
// montar e mostra original_event + stack_trace em <pre> com scroll.
type DrawerProps = {
  id: string;
  onClose: () => void;
  onReplay: () => void;
  onDiscard: () => void;
  onJumpToJob: (jobId: string) => void;
  actionInFlight: boolean;
};

function DeadLetterDrawer({
  id,
  onClose,
  onReplay,
  onDiscard,
  onJumpToJob,
  actionInFlight,
}: DrawerProps) {
  const [detail, setDetail] = useState<APIResult<DeadLetterDetail> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const r = await api.getDeadLetter(id);
      if (!cancelled) {
        setDetail(r);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Esc fecha o drawer.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const data = detail?.data ?? null;
  const replayDisabled = actionInFlight || !data || isHandled(data);

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-ink-900/40"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Detalhe da dead letter"
    >
      <div
        className="ks-scroll flex h-full w-full max-w-2xl flex-col overflow-y-auto bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-ink-200 bg-white px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold text-ink-900">Dead letter</h2>
            <p className="ks-code text-[11px] text-ink-500">{id}</p>
          </div>
          <button
            type="button"
            className="ks-btn-ghost text-xs"
            onClick={onClose}
            aria-label="Fechar"
          >
            fechar (esc)
          </button>
        </header>

        <div className="flex-1 space-y-4 px-4 py-4">
          {loading && <p className="text-sm text-ink-500">Carregando...</p>}
          {!loading && detail && !detail.ok && (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              HTTP {detail.status} — não foi possível carregar detalhes.
            </div>
          )}
          {data && (
            <>
              <section className="grid gap-2 sm:grid-cols-2">
                <Field label="error_code" value={data.error_code} mono />
                <Field
                  label="failed_at"
                  value={`${formatDateTime(data.failed_at)} (${formatRelativeTime(data.failed_at)})`}
                />
                <Field label="attempts" value={String(data.attempts)} />
                <Field label="worker_version" value={data.worker_version} />
                <Field
                  label="job_id"
                  value={data.job_id}
                  mono
                  action={
                    <button
                      type="button"
                      className="ks-btn-ghost text-[11px]"
                      onClick={() => onJumpToJob(data.job_id)}
                    >
                      abrir status →
                    </button>
                  }
                />
                <Field label="external_request_id" value={data.external_request_id} mono />
                <Field
                  label="correlation_id"
                  value={data.correlation_id ?? "—"}
                  mono={Boolean(data.correlation_id)}
                />
                {data.replayed_at && (
                  <Field
                    label="replayed_at"
                    value={`${formatDateTime(data.replayed_at)} → ${shortId(data.replayed_job_id)}`}
                  />
                )}
                {data.discarded_at && (
                  <Field
                    label="discarded_at"
                    value={`${formatDateTime(data.discarded_at)} — ${data.discard_reason ?? ""}`}
                  />
                )}
              </section>

              <section>
                <p className="ks-label">error_message</p>
                <pre className="ks-code mt-1 whitespace-pre-wrap break-words rounded-md border border-ink-200 bg-ink-50 px-3 py-2 text-ink-800">
                  {data.error_message}
                </pre>
              </section>

              <section>
                <p className="ks-label">original_event</p>
                <pre className="ks-code mt-1 max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-md border border-ink-200 bg-ink-50 px-3 py-2 text-ink-800">
                  {formatJSON(data.original_event)}
                </pre>
              </section>

              {data.stack_trace && (
                <section>
                  <p className="ks-label">stack_trace</p>
                  <pre className="ks-code mt-1 max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-rose-900">
                    {data.stack_trace}
                  </pre>
                </section>
              )}
            </>
          )}
        </div>

        <footer className="sticky bottom-0 flex items-center justify-end gap-2 border-t border-ink-200 bg-white px-4 py-3">
          <button
            type="button"
            className="ks-btn-secondary text-xs text-rose-700 hover:bg-rose-50"
            disabled={replayDisabled}
            onClick={onDiscard}
          >
            Discard
          </button>
          <button
            type="button"
            className="ks-btn-primary text-xs"
            disabled={replayDisabled}
            onClick={onReplay}
          >
            {actionInFlight ? "..." : "Replay"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  mono,
  action,
}: {
  label: string;
  value: string;
  mono?: boolean;
  action?: React.ReactNode;
}) {
  return (
    <div className="rounded-md border border-ink-200 px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <p className="ks-label">{label}</p>
        {action}
      </div>
      <p className={classNames("mt-0.5 text-sm text-ink-800 break-all", mono && "ks-code")}>
        {value}
      </p>
    </div>
  );
}

function ToastBanner({
  toast,
  onDismiss,
  onOpenJob,
}: {
  toast: Toast;
  onDismiss: () => void;
  onOpenJob: (id: string) => void;
}) {
  const kindCls =
    toast.kind === "success"
      ? "border-emerald-300 bg-emerald-50 text-emerald-900"
      : toast.kind === "error"
        ? "border-rose-300 bg-rose-50 text-rose-900"
        : "border-sky-300 bg-sky-50 text-sky-900";
  return (
    <div
      role="status"
      aria-live="polite"
      className={classNames(
        "fixed bottom-6 right-6 z-50 flex max-w-md items-start gap-3 rounded-lg border px-4 py-3 shadow-lg",
        kindCls,
      )}
    >
      <p className="flex-1 text-sm">{toast.message}</p>
      {toast.jobId && (
        <button
          type="button"
          className="ks-btn-ghost text-xs underline"
          onClick={() => onOpenJob(toast.jobId as string)}
        >
          abrir
        </button>
      )}
      <button
        type="button"
        className="ks-btn-ghost text-xs"
        onClick={onDismiss}
        aria-label="Fechar notificação"
      >
        ×
      </button>
    </div>
  );
}
